// Configuration.
//  * build time (VITE_*, public – never secrets): default API URL, fallback relays
//  * run time: everything else comes from the API (GET /api/config, GET /api/turn)
//
// Security: the API server can only be changed by the user in Cài đặt → Máy chủ.
// URL parameters cannot redirect the app to another server (that would allow
// phishing links pointing to a fake login).

const env = import.meta.env;

const list = (v: string | undefined | null) =>
  (v ?? '').split(',').map((s) => s.trim()).filter(Boolean);

export const APP_ID = 'photobooth-online-v1';
/** Phase 1: exactly 2 people per room (host + 1 guest). Raise later for mesh/SFU. */
export const MAX_PEERS = 2;

export const PUBLIC_URL: string = env.VITE_PUBLIC_URL || '';

/** Test builds only (VITE_ALLOW_URL_OVERRIDES=1): `?relay=` for the two-browser e2e test. */
const urlOverrides = env.VITE_ALLOW_URL_OVERRIDES === '1';
const urlParam = (k: string) => {
  if (!urlOverrides) return undefined;
  try { return new URLSearchParams(location.search).get(k) ?? undefined; } catch { return undefined; }
};

const store = {
  get(k: string) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string | null) {
    try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* private mode */ }
  },
};

const API_KEY = 'booth.apiUrl';

/** Booth API base URL ('' = same origin, e.g. Vite proxy in dev). */
export function apiUrl(): string {
  return (store.get(API_KEY) ?? env.VITE_API_URL ?? '').replace(/\/$/, '');
}

export function setApiUrl(url: string | null) {
  const v = url?.trim().replace(/\/$/, '') ?? null;
  if (v && !/^https:\/\//.test(v) && !/^http:\/\/(localhost|127\.0\.0\.1|10\.|192\.168\.)/.test(v))
    throw new Error('Máy chủ phải dùng https:// (http chỉ cho mạng nội bộ khi phát triển)');
  store.set(API_KEY, v || null);
  remote = null;
}

export interface RemoteConfig {
  issuer: string;
  clientId: string;
  relays: string[];
  turn: boolean;
  identityProviders: string[];
  paywall: boolean;
}

let remote: Promise<RemoteConfig | null> | null = null;

/** Public bootstrap config from the API; null when no server is reachable. */
export function remoteConfig(): Promise<RemoteConfig | null> {
  remote ??= (async () => {
    try {
      const r = await fetch(`${apiUrl()}/api/config`, { signal: AbortSignal.timeout(5000) });
      if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return null;
      return (await r.json()) as RemoteConfig;
    } catch {
      return null;
    }
  })();
  const p = remote;
  void p.then((c) => { if (!c && remote === p) remote = null; }); // retry next time
  return p;
}

const STUN: RTCIceServer = { urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] };
let turn: RTCIceServer | null = null;

/** Short-lived TURN credentials are fetched by the API client after login. */
export function setTurn(t: RTCIceServer | null) {
  turn = t;
}

export function iceServers(): RTCIceServer[] {
  return turn ? [STUN, turn] : [STUN];
}

/** Signaling relays: test override → server config → build-time fallback → Trystero defaults. */
export async function signalingRelays(): Promise<string[]> {
  const o = list(urlParam('relay'));
  if (o.length) return o;
  const c = await remoteConfig();
  if (c?.relays.length) return c.relays;
  return list(env.VITE_NOSTR_RELAYS);
}

/** Resolution the segmenter + outgoing stream run at. Small = low latency. */
export const PROC_WIDTH = 480;
/** Outgoing frame rate. */
export const STREAM_FPS = 30;
/** Max outgoing video bitrate (packed frame is 2x wide). */
export const MAX_BITRATE = 2_500_000;

export const assetUrl = (p: string) => new URL(p, document.baseURI).href;
